import React, { useState, useMemo } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Box,
  Badge
} from '@amzn/awsui-components-console';
import CodeView from '@amzn/awsui-code-view-components-console/code-view';
import { createHighlight } from '@amzn/awsui-code-view-components-console/highlight';
import { SqlHighlightRules } from 'ace-code/src/mode/sql_highlight_rules';
import { PythonHighlightRules } from 'ace-code/src/mode/python_highlight_rules';
import './CodeBlock.scss';

export interface CodeBlockMetadata {
  tables?: string[];
  estimatedRows?: number | string;
  [key: string]: any;
}

export interface CodeBlockProps {
  language: 'sql' | 'pyspark' | 'python';
  code: string;
  actions?: Array<'copy' | 'save' | 'download' | 'execute'>;
  metadata?: CodeBlockMetadata;
  state?: 'default' | 'copied' | 'generating' | 'validated';
  onCopy?: () => void;
  onSave?: () => void;
  onDownload?: () => void;
  onExecute?: () => void;
}

/**
 * CodeBlock - Syntax-highlighted code display with actions
 * 
 * Displays generated code, pasted SQL, or validated queries with:
 * - Syntax highlighting for SQL and PySpark
 * - Copy, save, download, execute actions
 * - Metadata display (tables, estimated rows)
 * - State indicators (validated, generating, copied)
 */
export const CodeBlock: React.FC<CodeBlockProps> = ({
  language,
  code,
  actions = ['copy', 'save'],
  metadata,
  state = 'default',
  onCopy,
  onSave,
  onDownload,
  onExecute
}) => {
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');

  // Create highlight function based on language
  const highlight = useMemo(() => {
    if (language === 'sql') {
      return createHighlight(new SqlHighlightRules());
    } else {
      // For python and pyspark
      return createHighlight(new PythonHighlightRules());
    }
  }, [language]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopyState('copied');
      if (onCopy) onCopy();
      
      // Reset after 2 seconds
      setTimeout(() => {
        setCopyState('idle');
      }, 2000);
    } catch (error) {
      console.error('Failed to copy code:', error);
    }
  };

  const handleSave = () => {
    if (onSave) onSave();
  };

  const handleDownload = () => {
    // Create a blob and download
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `code.${language === 'sql' ? 'sql' : 'py'}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    if (onDownload) onDownload();
  };

  const handleExecute = () => {
    if (onExecute) onExecute();
  };

  const getLanguageLabel = (): string => {
    switch (language) {
      case 'sql':
        return 'SQL';
      case 'pyspark':
        return 'PySpark';
      case 'python':
        return 'Python';
      default:
        return language.toUpperCase();
    }
  };

  const isValidated = state === 'validated';
  const isGenerating = state === 'generating';
  const isCopied = state === 'copied' || copyState === 'copied';

  return (
    <div className={`code-block code-block--${state}`}>
      <Container
        header={
          <Header
            variant="h3"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                {actions.includes('copy') && (
                  <Button
                    iconName={isCopied ? 'check' : 'copy'}
                    variant="icon"
                    onClick={handleCopy}
                    ariaLabel="Copy code"
                  >
                    {isCopied ? 'Copied' : 'Copy'}
                  </Button>
                )}
                {actions.includes('save') && (
                  <Button
                    iconName="upload"
                    variant="icon"
                    onClick={handleSave}
                    ariaLabel="Save code"
                  >
                    Save
                  </Button>
                )}
                {actions.includes('download') && (
                  <Button
                    iconName="download"
                    variant="icon"
                    onClick={handleDownload}
                    ariaLabel="Download code"
                  >
                    Download
                  </Button>
                )}
                {actions.includes('execute') && (
                  <Button
                    iconName="caret-right-filled"
                    variant="primary"
                    onClick={handleExecute}
                    loading={isGenerating}
                    ariaLabel="Execute code"
                  >
                    Execute
                  </Button>
                )}
              </SpaceBetween>
            }
          >
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <span>{getLanguageLabel()}</span>
              {isValidated && (
                <Badge color="green">Valid SQL</Badge>
              )}
              {isGenerating && (
                <Badge color="blue">Generating...</Badge>
              )}
            </SpaceBetween>
          </Header>
        }
      >
        <SpaceBetween size="m">
          {/* Code Display using Cloudscape CodeView */}
          <div className="code-block__code">
            <CodeView
              content={code}
              highlight={highlight}
              lineNumbers={code.split('\n').length > 5}
              wrapLines
            />
          </div>

          {/* Metadata */}
          {metadata && (
            <Box variant="awsui-key-label">
              <SpaceBetween size="xs">
                {metadata.tables && metadata.tables.length > 0 && (
                  <div className="code-block__metadata-item">
                    <span className="code-block__metadata-label">Tables:</span>{' '}
                    <span className="code-block__metadata-value">
                      {metadata.tables.join(', ')}
                    </span>
                  </div>
                )}
                {metadata.estimatedRows !== undefined && (
                  <div className="code-block__metadata-item">
                    <span className="code-block__metadata-label">Est. rows:</span>{' '}
                    <span className="code-block__metadata-value">
                      ~{metadata.estimatedRows}
                    </span>
                  </div>
                )}
              </SpaceBetween>
            </Box>
          )}
        </SpaceBetween>
      </Container>
    </div>
  );
};

export default CodeBlock;
